import { useState } from "react";
import { Dialog } from "primereact/dialog";
import SaldoProjetadoBpService from "../../../services/resource/saldoProjetadoBpService";
import { formatarDataHora, formatarMoeda } from "../../../utils/solicitacoes/formatters";
import { classeSaldo } from "../../../utils/solicitacoes/orcamento";
import StatusBadge from "./StatusBadge";

const service = new SaldoProjetadoBpService();

// Rótulo local (sem importar processoRegistry, que importa os formulários que usam este botão).
const ROTULO_PROCESSO = {
    LANCAMENTO_DESPESA: "Lançamento de Despesa",
    SOLICITACAO_COMPRAS: "Solicitação de Compras",
};

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

/**
 * Botão por conta BP (linha de rateio) que abre o saldo projetado do mês: orçado menos tudo que já
 * foi solicitado no MyDash (Lançamento de Despesa + Solicitação de Compras) pra essa conta —
 * complementa o orçado x realizado, que só enxerga o que já virou lançamento no Fortes.
 */
export default function SaldoProjetadoBpBotao({ baseBpId }) {
    const [aberto, setAberto] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(false);

    if (!baseBpId) return null;

    function abrir() {
        setAberto(true);
        setCarregando(true);
        setErro(false);
        service.consultar(baseBpId)
            .then(res => setDados(res.data))
            .catch(() => { setErro(true); setDados(null); })
            .finally(() => setCarregando(false));
    }

    return (
        <>
            <button
                type="button"
                title="Consultar saldo projetado (o que já foi lançado no MyDash)"
                onClick={abrir}
                className="h-7 w-7 inline-flex items-center justify-center rounded-full text-primary hover:bg-primary/10"
            >
                <i className="fa fa-chart-line" />
            </button>

            <Dialog
                visible={aberto}
                onHide={() => setAberto(false)}
                header="Saldo projetado da conta BP"
                style={{ width: "90%", maxWidth: "1200px" }}
                blockScroll
                contentClassName="pt-2"
            >
                {carregando && (
                    <div className="flex items-center justify-center py-10">
                        <i className="fa fa-spinner fa-spin text-xl text-muted-foreground" />
                    </div>
                )}

                {!carregando && erro && (
                    <p className="text-sm text-destructive text-center py-8">Não foi possível consultar o saldo projetado.</p>
                )}

                {!carregando && dados && (
                    <div className="flex flex-col gap-4 pt-1">
                        <div className="text-sm text-foreground">
                            <div className="font-semibold">{dados.contaReduzido} · {dados.contaDescricao}</div>
                            <div className="text-muted-foreground text-xs">
                                {dados.filialNome} · C. Custo {dados.centroCustoCodigo} · {dados.centroCustoDescricao}
                            </div>
                            <div className="text-muted-foreground text-xs mt-0.5">
                                Mês de referência: {rotuloMesReferencia(dados)}
                            </div>
                        </div>

                        <SaldoProjetadoResumo dados={dados} />
                    </div>
                )}
            </Dialog>
        </>
    );
}

export function rotuloMesReferencia(dados) {
    return `${MESES[dados.mes - 1]}/${dados.ano}`;
}

/**
 * Indicadores + lista de solicitações do mês de referência. Reaproveitado pelo modal "Conta BP"
 * (RateioOrcamentarioTabela), que já mostra o orçado x realizado de todos os meses.
 */
export function SaldoProjetadoResumo({ dados }) {
    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Indicador rotulo="Orçado" valor={dados.orcado} />
                <Indicador rotulo="Compras" valor={dados.totalCompras} />
                <Indicador rotulo="Despesas" valor={dados.totalDespesas} />
                <Indicador rotulo="Total MyDash" valor={dados.totalMydash} />
                <Indicador
                    rotulo="Saldo"
                    valor={dados.saldoProjetado}
                    classe={`${classeSaldo(dados.saldoProjetado, dados.orcado)} font-bold`}
                    destaque
                />
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
                Saldo projetado = valor orçado − (Solicitações de Compras + Lançamentos de Despesa) do mês, sem as canceladas.
            </p>

            {dados.solicitacoes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhuma solicitação lançou valor nesta conta no mês de referência.
                </p>
            ) : (
                <div className="border border-border rounded-lg overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-muted">
                            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                <th className="px-3 py-2 font-semibold">Solicitação</th>
                                <th className="px-3 py-2 font-semibold">Processo</th>
                                <th className="px-3 py-2 font-semibold">Situação</th>
                                <th className="px-3 py-2 font-semibold">Solicitante</th>
                                <th className="px-3 py-2 font-semibold">Nota Fiscal</th>
                                <th className="px-3 py-2 font-semibold">Criada em</th>
                                <th className="px-3 py-2 font-semibold text-right">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dados.solicitacoes.map((s, i) => (
                                <tr key={`${s.solicitacaoId}-${i}`} className="border-t border-border">
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <a
                                            href={`/solicitacoes/${s.solicitacaoId}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-semibold text-primary hover:underline"
                                        >
                                            {s.numero}
                                        </a>
                                        {s.descricao && (
                                            <div className="text-[11px] text-muted-foreground max-w-[220px] truncate" title={s.descricao}>
                                                {s.descricao}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">{ROTULO_PROCESSO[s.tipoProcesso] ?? s.tipoProcesso}</td>
                                    <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                                    <td className="px-3 py-2 whitespace-nowrap">{s.solicitanteNome}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{s.numeroNotaFiscal || "-"}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{formatarDataHora(s.dataCriacao)}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap font-medium">{formatarMoeda(s.valor)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Indicador({ rotulo, valor, classe = "", destaque = false }) {
    return (
        <div className={`rounded-lg border px-3 py-2 ${destaque ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{rotulo}</div>
            <div className={`text-sm text-foreground ${classe}`}>{formatarMoeda(valor)}</div>
        </div>
    );
}
